"use client";
import IconLabel from "@/icons/IconLabel";
import { hasWebsiteIcon } from "@/icons/icon-catalogue";

import Link from "next/link";
import type { CtaComponentProps } from "@/cms/models";
import { surfaceStyle, typographyStyle } from "@/utils/visual-style";
import { openContentDialog } from "./ContentDialog";
import PageDialogLink from "@/components/page-dialog/PageDialogLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/sharp-light-svg-icons";
import { typographyRoleStyle } from "@/utils/website-typography";

/** With `onClick` the action is a button that acts on the page instead of a link. */
export default function DesignAction({
  config,
  onClick,
  "aria-haspopup": hasPopup,
}: {
  config: Partial<CtaComponentProps> & { link: string; label: string };
  onClick?: () => void;
  "aria-haspopup"?: "dialog";
}) {
  const states = config.lookSystem && (config.variant || "pill") === "pill";
  const props = {
    className: `re-action re-action-${config.variant || "pill"} ${config.surface ? "re-surface" : ""}`,
    "data-re-states": states ? "" : undefined,
    style: {
      color: config.variant === "arrow" ? undefined : config.textColor,
      backgroundColor:
        config.variant === "arrow" || config.surface
          ? undefined
          : config.bgColor,
      ...surfaceStyle(config.surface, config.bgColor),
      ...typographyStyle(config.typography),
      ...(config.variant === "arrow"
        ? typographyRoleStyle("button", config.typography)
        : {}),
      "--re-action-bg": config.variant === "arrow" ? config.bgColor : undefined,
      "--re-action-color":
        config.variant === "arrow" ? config.textColor : undefined,
      "--re-action-hover-bg": config.hoverBgColor,
      "--re-action-hover-color": config.hoverTextColor,
      "--re-action-active-bg": states ? config.activeBgColor : undefined,
      "--re-action-active-color": states ? config.activeTextColor : undefined,
    } as React.CSSProperties,
  };
  const label = (
    <>
      <IconLabel
        iconId={config.iconId}
        position={config.iconPosition}
        lookSystem={config.lookSystem}
      >
        {config.label}
      </IconLabel>
      {config.variant === "arrow" && !hasWebsiteIcon(config.iconId) && (
        <FontAwesomeIcon
          icon={faChevronRight}
          aria-hidden
          data-re-card-arrow={config.lookSystem ? "" : undefined}
        />
      )}
    </>
  );
  return onClick || config.action?.type === "dialog" ? (
    <button
      type="button"
      {...props}
      aria-haspopup={hasPopup}
      onClick={onClick ?? (() => openContentDialog(config.action!.targetId))}
    >
      {label}
    </button>
  ) : config.pageDialog ? (
    <PageDialogLink href={config.link} dialog={config.pageDialog} {...props}>
      {label}
    </PageDialogLink>
  ) : (
    <Link href={config.link} {...props}>
      {label}
    </Link>
  );
}
